import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { SidebarTradeCard } from '../../context/ProfileTradeSidebarContext';

interface TradeSidebarCardProps {
  card: SidebarTradeCard;
  onUpdate: (id: string, updates: Partial<SidebarTradeCard>) => void;
  onRemove: (id: string) => void;
  onCalculatingChange?: (id: string, isCalculating: boolean) => void;
}

// Condition options
const CONDITION_OPTIONS = [
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
];

// Language options
const LANGUAGE_OPTIONS = [
  { value: 'EN', label: 'EN' },
  { value: 'FR', label: 'FR' },
  { value: 'DE', label: 'DE' },
  { value: 'ES', label: 'ES' },
  { value: 'IT', label: 'IT' },
  { value: 'JP', label: 'JP' },
];

export const TradeSidebarCard: React.FC<TradeSidebarCardProps> = ({
  card,
  onUpdate,
  onRemove,
  onCalculatingChange,
}) => {
  const [calculatePrice] = useCalculatePriceMutation();
  const [isCalculating, setIsCalculating] = useState(false);
  const hasInitialFetch = useRef(false);

  // Always recalculate price on mount to ensure displayed price matches card parameters
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchNewPrice({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get small image URL from the card's image
  const getSmallImageUrl = (url: string): string => {
    return url
      ?.replace('/large/', '/small/')
      ?.replace('/normal/', '/small/') || url;
  };

  // Calculate price from server when card properties change
  const fetchNewPrice = async (updates: Partial<SidebarTradeCard>) => {
    const updatedCard = { ...card, ...updates };

    try {
      setIsCalculating(true);
      onCalculatingChange?.(card.id, true);
      const result = await calculatePrice({
        cards: [{
          wantListId: updatedCard.wantListId,
          cardId: updatedCard.cardId?.toString(),
          condition: updatedCard.condition,
          language: updatedCard.language,
          foil: updatedCard.foil,
          set: updatedCard.set,
        }]
      }).unwrap();

      const newPrice = result.cards[0]?.displayPrice;
      if (newPrice !== undefined) {
        onUpdate(card.id, { ...updates, pointValue: newPrice });
      } else {
        onUpdate(card.id, updates);
      }
    } catch (error) {
      console.error('Failed to calculate price:', error);
      onUpdate(card.id, updates);
    } finally {
      setIsCalculating(false);
      onCalculatingChange?.(card.id, false);
    }
  };

  // Handle set change
  const handleSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const selectedSetDetail = card.acceptableSetDetails?.find(s => {
      const optionValue = s.cardId ? `${s.code}-${s.cardId}` : s.code;
      return optionValue === selectedValue;
    });

    if (selectedSetDetail) {
      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

      const updates: Partial<SidebarTradeCard> = {
        setCode: selectedSetDetail.code,
        set: selectedSetDetail.name,
        availableInFoil: newAvailableInFoil,
        availableInNonFoil: newAvailableInNonFoil,
      };

      if (selectedSetDetail.cardId) {
        updates.cardId = selectedSetDetail.cardId;
      }

      if (selectedSetDetail.imageUrl) {
        updates.imageUrl = selectedSetDetail.imageUrl;
      }

      // Auto-switch foil if current selection becomes invalid
      const userAcceptsFoil = card.acceptFoil ?? true;
      const userAcceptsNonFoil = card.acceptNonFoil ?? true;
      const canOfferFoil = newAvailableInFoil && userAcceptsFoil;
      const canOfferNonFoil = newAvailableInNonFoil && userAcceptsNonFoil;

      if (card.foil && !canOfferFoil && canOfferNonFoil) {
        updates.foil = false;
      } else if (!card.foil && !canOfferNonFoil && canOfferFoil) {
        updates.foil = true;
      }

      fetchNewPrice(updates);
    }
  };

  // Handle foil change
  const handleFoilChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedFoil = e.target.value === 'foil';

    // Validate against availability
    const physicallyAvailableInFoil = card.availableInFoil ?? false;
    const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
    const userAcceptsFoil = card.acceptFoil ?? true;
    const userAcceptsNonFoil = card.acceptNonFoil ?? true;

    const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
    const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;

    if (requestedFoil && !canOfferFoil) {
      toast.warning('This card is not available in foil');
      return;
    }
    if (!requestedFoil && !canOfferNonFoil) {
      toast.warning('This card is not available in non-foil');
      return;
    }

    fetchNewPrice({ foil: requestedFoil });
  };

  // Handle condition change
  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    fetchNewPrice({ condition: e.target.value });
  };

  // Handle language change
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(card.id, { language: e.target.value });
  };

  // Determine foil availability for dropdown
  const physicallyAvailableInFoil = card.availableInFoil ?? false;
  const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
  const userAcceptsFoil = card.acceptFoil ?? true;
  const userAcceptsNonFoil = card.acceptNonFoil ?? true;
  const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
  const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  // Get available conditions
  const acceptableConditions = card.acceptableConditions || CONDITION_OPTIONS.map(c => c.value);
  const availableConditions = CONDITION_OPTIONS.filter(c => acceptableConditions.includes(c.value));

  // Determine if we should show set dropdown
  const hasMultipleSets = card.acceptableSetDetails && card.acceptableSetDetails.length > 1;

  // Current set value for dropdown
  const currentSetValue = card.cardId
    ? `${card.setCode}-${card.cardId}`
    : card.setCode;

  return (
    <div className={`trade-sidebar-card ${card.isInvalid ? 'invalid' : ''}`}>
      {/* Card thumbnail */}
      <div className="trade-sidebar-card-image">
        <FoilCardImage
          src={getSmallImageUrl(card.imageUrl)}
          alt={card.cardName}
          foil={card.foil}
          borderRadius="4px"
          style={{ width: '60px', height: '84px', objectFit: 'cover' }}
        />
      </div>

      {/* Card details */}
      <div className="trade-sidebar-card-details">
        {/* Card name */}
        <div className="trade-sidebar-card-name" title={card.cardName}>
          {card.cardName}
        </div>

        {/* Dropdowns row */}
        <div className="trade-sidebar-card-dropdowns">
          {/* Set dropdown */}
          {hasMultipleSets ? (
            <select
              value={currentSetValue}
              onChange={handleSetChange}
              className="trade-sidebar-select set-select"
              disabled={isCalculating}
            >
              {[...card.acceptableSetDetails!]
                .sort((a, b) => {
                  const nameCompare = a.name.localeCompare(b.name);
                  if (nameCompare !== 0) return nameCompare;
                  const aNum = parseInt(a.collectorNumber || '0', 10);
                  const bNum = parseInt(b.collectorNumber || '0', 10);
                  return aNum - bNum;
                })
                .map(setDetail => {
                  const uniqueValue = setDetail.cardId
                    ? `${setDetail.code}-${setDetail.cardId}`
                    : setDetail.code;
                  const displayText = setDetail.collectorNumber
                    ? `${setDetail.name} #${setDetail.collectorNumber}`
                    : setDetail.name;
                  return (
                    <option key={uniqueValue} value={uniqueValue}>
                      {displayText}
                    </option>
                  );
                })}
            </select>
          ) : (
            <span className="trade-sidebar-card-set-text">{card.set}</span>
          )}

          {/* Foil dropdown */}
          {showFoilDropdown ? (
            <select
              value={card.foil ? 'foil' : 'non-foil'}
              onChange={handleFoilChange}
              className="trade-sidebar-select foil-select"
              disabled={isCalculating}
            >
              <option value="non-foil">Non-Foil</option>
              <option value="foil">Foil</option>
            </select>
          ) : (
            <span className="trade-sidebar-card-foil-text">
              {canOfferFoil ? 'Foil' : 'Non-Foil'}
            </span>
          )}

          {/* Condition dropdown */}
          {availableConditions.length > 1 ? (
            <select
              value={card.condition}
              onChange={handleConditionChange}
              className="trade-sidebar-select condition-select"
              disabled={isCalculating}
            >
              {availableConditions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="trade-sidebar-card-condition-text">
              {availableConditions[0]?.label || 'NM'}
            </span>
          )}

          {/* Language dropdown */}
          <select
            value={card.language}
            onChange={handleLanguageChange}
            className="trade-sidebar-select language-select"
          >
            {LANGUAGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div className="trade-sidebar-card-price">
          {isCalculating ? (
            <span className="trade-sidebar-card-price-loading">...</span>
          ) : (
            <Points points={card.pointValue} type="small" />
          )}
        </div>
      </div>

      {/* Remove button */}
      <button
        className="trade-sidebar-card-remove"
        onClick={() => onRemove(card.id)}
        aria-label="Remove card"
      >
        <img
          src={AssetUrls.icons.dropdowns.close}
          alt="Remove"
          className="trade-sidebar-card-remove-icon"
        />
      </button>

      {/* Invalid card overlay */}
      {card.isInvalid && (
        <div className="trade-sidebar-card-invalid-overlay">
          <span>No longer available</span>
        </div>
      )}
    </div>
  );
};

export default TradeSidebarCard;
